import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Email } from '../../types/email';
import { API_BASE_URL, FILE_BASE_URL } from '../../config';
import { getFileUrl } from '../../utils/urlHelper';

// Estilos CSS para la animación de carga
const loadingStyles = `
  @keyframes loading-pulse {
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
  }
`;

// Agregar estilos al documento si no existen
if (!document.getElementById('email-loading-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'email-loading-styles';
  styleSheet.textContent = loadingStyles;
  document.head.appendChild(styleSheet);
}

interface EmailListProps {
  folder: string;
  searchQuery: string;
  onEmailSelect: (email: Email) => void;
  selectedEmail: Email | null;
  paciente?: any | null;
  emails?: any[];
  loading?: boolean;
  hasEmailsLoaded?: boolean;
  folderSelected?: boolean;
  updateEmailFlag?: (emailId: string, flagged: boolean) => void;
  onDeleteEmail?: (emailId: string) => void;
  onRestoreEmail?: (emailId: string) => void;
  loadingFolders?: Record<string, boolean>;
  loadingEmailContent?: string | null;
}

// Función helper para sanitizar HTML básico
const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  
  // Remover scripts y iframes
  let sanitized = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
  
  // Permitir solo tags seguros
  const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'blockquote', 'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody'];
  const allowedAttrs = ['href', 'title', 'target', 'src', 'alt', 'class', 'style'];
  
  // Esta es una sanitización básica - en producción usar DOMPurify
  return sanitized;
};

// Nueva función para extraer texto plano de HTML
const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  // Eliminar imágenes cid y tags img
  let sanitized = html.replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi, '');
  // Quitar tags y decodificar entidades básicas
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeHtml(sanitized);
  return tmp.textContent || tmp.innerText || '';
};

const EmailList: React.FC<EmailListProps> = ({
  folder,
  searchQuery,
  onEmailSelect,
  selectedEmail,
  paciente,
  emails = [],
  loading = false,
  hasEmailsLoaded = true,
  folderSelected = false,
  updateEmailFlag,
  onDeleteEmail,
  onRestoreEmail,
  loadingFolders = {},
  loadingEmailContent
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<Email | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filtrar emails según la carpeta y búsqueda
  const filteredEmails = emails.filter(email => {
    // Validar que el email tenga id
    if (!email || !email.id) {
      console.warn('[WARNING] Email sin id válido encontrado:', email);
      return false;
    }
    
    // Para "Todos", mostrar solo emails que NO estén en papelera
    const matchesFolder = folder === 'todos' 
      ? email.folder !== 'papelera' 
      : email.folder === folder;
    
    const matchesSearch = !searchQuery || 
      email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.from?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.from?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.bodyPreview?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesFolder && matchesSearch;
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) {
        console.warn('[WARNING] Fecha inválida recibida:', dateString);
        return 'Fecha no disponible';
      }
      
      // Calcular diferencia en milisegundos
      const diffMs = now.getTime() - date.getTime();
      
      // Si la diferencia es negativa (fecha futura), mostrar como "hoy"
      if (diffMs < 0) {
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      }
      
      // Verificar si es hoy comparando solo la fecha (sin hora)
      const isToday = date.toDateString() === now.toDateString();
      const diffInHours = diffMs / (1000 * 60 * 60);
      const diffInDays = Math.floor(diffInHours / 24);
      
      // Formato "D mes" para mostrar en la lista (como Gmail)
      const formatDateForList = (date: Date) => {
        const day = date.getDate();
        const month = date.toLocaleDateString('es-ES', { month: 'short' });
        return `${day} ${month}`;
      };
      
      if (isToday || diffInDays === 0) {
        // Si es hoy o hace menos de 24 horas, mostrar la hora
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      } else if (diffInHours < 168) { // Menos de 7 días
        return date.toLocaleDateString('es-ES', { weekday: 'short' });
      } else {
        return formatDateForList(date);
      }
    } catch (error) {
      console.error('[ERROR] Error al formatear fecha:', error, dateString);
      return 'Fecha no disponible';
    }
  };

  const getInitials = (name: string) => {
    return (name || '')
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleEmailClick = (email: Email) => {
    if (!email || !email.id) {
      console.warn('[WARNING] handleEmailClick called with null or invalid email:', email);
      return;
    }
    onEmailSelect(email);
  };

  const handleStarClick = async (email: Email, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!updateEmailFlag) return;
    const nuevoValor = !email.isFlagged;
    updateEmailFlag(email.id, nuevoValor); // Optimista
    try {
      await fetch(`${API_BASE_URL}/marcar-flag`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message_id: email.id, flagged: nuevoValor })
      });
    } catch (e) {
      updateEmailFlag(email.id, !nuevoValor); // Revertir si falla
    }
  };

  const handleActionClick = (email: Email, event: React.MouseEvent) => {
    event.stopPropagation();
    setEmailToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!emailToDelete) return;
    
    setDeleting(true);
    try {
      // Usar la función correcta según la carpeta
      if (folder === 'papelera' && onRestoreEmail) {
        onRestoreEmail(emailToDelete.id);
      } else if (onDeleteEmail) {
        onDeleteEmail(emailToDelete.id);
      }
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch (error) {
      console.error('Error en la acción:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelAction = () => {
    setDeleteDialogOpen(false);
    setEmailToDelete(null);
  };

  const noPaciente = !paciente || !paciente.id;
  const noCarpeta = !folderSelected || !folder || folder === '';
  const showSelectPacienteMsg = noPaciente;
  const showSelectCarpetaMsg = !noPaciente && noCarpeta;
  const isFolderLoading = loadingFolders[folder] || false;

  if (loading || isFolderLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1.5, borderRadius: 1.5 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {filteredEmails.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'flex-start',
          minHeight: 180,
          pt: 5,
          p: 3
        }}>
          <InfoIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {showSelectPacienteMsg
              ? 'Selecciona un paciente'
              : showSelectCarpetaMsg
                ? 'Selecciona una carpeta'
                : 'No hay emails'}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {showSelectPacienteMsg
              ? 'Primero selecciona un paciente para ver sus emails.'
              : showSelectCarpetaMsg
                ? 'Primero selecciona una carpeta para ver los emails.'
                : searchQuery || paciente
                  ? `No se encontraron emails que coincidan con los filtros seleccionados.`
                  : `No hay emails en ${folder === 'inbox' ? 'la bandeja de entrada' : folder}`
            }
          </Typography>
        </Box>
      ) : (
        <List dense>
          {filteredEmails
            .filter(email => email && email.id) // Filtrar emails sin id válido
            .map((email) => (
            <ListItem
              key={email.id}
              disablePadding
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none'
                }
              }}
            >
              <ListItemButton
                selected={selectedEmail?.id === email.id}
                onClick={() => handleEmailClick(email)}
                disabled={loading || isFolderLoading}
                sx={{
                  py: 1.5,
                  position: 'relative',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                    '& .MuiTypography-root': {
                      color: '#f5f6fa !important',
                      fontWeight: 600,
                    },
                    '& .MuiAvatar-root': {
                      border: '2px solid',
                      borderColor: '#f5f6fa',
                      color: '#f5f6fa',
                      backgroundColor: 'primary.main',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#f5f6fa !important',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  // Indicador sutil de carga
                  ...(loadingEmailContent === email.id && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      backgroundColor: 'primary.main',
                      animation: 'loading-pulse 1.5s ease-in-out infinite',
                    }
                  })
                }}
              >
                {/* Avatar del paciente o doctor */}
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                        {['enviados', 'borradores'].includes(email.folder) || (['papelera', 'spam'].includes(email.folder) && email.from?.email === 'drduran@hotmail.com.ar') ? 'Dr. César Duran' : (paciente?.nombre || 'Paciente')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}>
                        {email.from?.email}
                      </Typography>
                      {paciente?.email2 && email.from?.email === paciente.email2 && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}>
                          Correo secundario del paciente
                        </Typography>
                      )}
                    </Box>
                  }
                >
                  <Avatar
                    src={['enviados', 'borradores'].includes(email.folder) || (['papelera', 'spam'].includes(email.folder) && email.from?.email === 'drduran@hotmail.com.ar') ? undefined : getFileUrl(paciente?.ruta_imagen_perfil)}
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      mr: 2,
                      fontSize: '0.75rem',
                      bgcolor: email.isRead 
                        ? (paciente?.email2 && email.from?.email === paciente.email2 ? 'grey.500' : 'grey.400')
                        : (paciente?.email2 && email.from?.email === paciente.email2 ? 'secondary.main' : 'primary.main'),
                      cursor: 'pointer',
                      border: paciente?.email2 && email.from?.email === paciente.email2 ? '1px solid' : 'none',
                      borderColor: 'secondary.light',
                      // Aplicar filtro grisesito cuando no está seleccionado
                      filter: selectedEmail?.id === email.id ? 'none' : 'grayscale(30%) opacity(0.8)',
                      transition: 'filter 0.2s ease-in-out',
                      '&:hover': {
                        filter: 'grayscale(10%) opacity(0.9)',
                      }
                    }}
                  >
                    {['enviados', 'borradores'].includes(email.folder) || (['papelera', 'spam'].includes(email.folder) && email.from?.email === 'drduran@hotmail.com.ar') ? 'JC' : (paciente?.nombre ? paciente.nombre[0].toUpperCase() : 'P')}
                  </Avatar>
                </Tooltip>

                {/* Contenido del email */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: email.isRead ? 400 : 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}
                    >
                      {email.asunto || email.subject}
                    </Typography>
                    
                    {/* Chips de NO LEÍDO, BORRADOR y SECUNDARIO antes de la fecha */}
                    {!email.isRead && (
                      <Chip
                        label="No leído"
                        size="small"
                        color="primary"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          ml: 1,
                          bgcolor: 'primary.main',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5,
                            py: 0.25
                          }
                        }}
                      />
                    )}
                    {folder === 'todos' && email.folder === 'borradores' && (
                      <Chip
                        label="Borrador"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem', 
                          ml: 1,
                          borderColor: selectedEmail?.id === email.id ? 'white' : 'grey.400',
                          color: selectedEmail?.id === email.id ? 'white' : 'grey.600',
                          bgcolor: 'transparent',
                          '& .MuiChip-label': {
                            px: 0.5,
                            py: 0.25
                          }
                        }}
                      />
                    )}
                    {paciente?.email2 && email.from?.email === paciente.email2 && (
                      <Chip
                        label="Secundario"
                        size="small"
                        sx={{ 
                          height: 18, 
                          fontSize: '0.6rem', 
                          ml: 1,
                          bgcolor: selectedEmail?.id === email.id ? 'rgba(255,255,255,0.2)' : 'grey.600',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: 0.5,
                            py: 0.25
                          }
                        }}
                      />
                    )}
                    {/* Fecha del email */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        ml: 1,
                        minWidth: 'fit-content',
                        fontSize: '0.75rem'
                      }}
                    >
                      {email.receivedDateTime ? formatDate(email.receivedDateTime) : ''}
                    </Typography>
                    
                    {email.adjuntos || email.hasAttachments ? (
                      <Tooltip title="Tiene adjuntos">
                        <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      '&.Mui-selected': {
                        color: 'primary.dark',
                      }
                    }}
                  >
                    {email.bodyPreview
                      ? (htmlToPlainText(email.bodyPreview).substring(0, 100) + (htmlToPlainText(email.bodyPreview).length > 100 ? '...' : ''))
                      : (email.asunto || email.subject || '')}
                  </Typography>
                </Box>

                {/* Indicadores y acciones */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <Tooltip title={email.isFlagged ? "Quitar de importantes" : "Marcar como importante"}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleStarClick(email, e)}
                      sx={{ mr: 0.5 }}
                    >
                      {email.isFlagged ? (
                        <StarIcon style={{ color: '#FFD600' }} />
                      ) : (
                        <StarBorderIcon />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={folder === 'papelera' ? "Restaurar" : "Eliminar"}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleActionClick(email, e)}
                    >
                      {folder === 'papelera' ? (
                        <RestoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      
      {/* Diálogo de confirmación para eliminar/restaurar */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelAction}>
        <DialogTitle>
          {folder === 'papelera' ? 'Confirmar restauración' : 'Confirmar eliminación'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {folder === 'papelera' 
              ? `¿Estás seguro de que quieres restaurar el email "${emailToDelete?.subject}"?`
              : `¿Estás seguro de que quieres eliminar el email "${emailToDelete?.subject}"?`
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {folder === 'papelera'
              ? 'El email se moverá de vuelta a la bandeja de entrada.'
              : 'El email se moverá a la papelera. Puedes recuperarlo desde allí si es necesario.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} disabled={deleting}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmAction} 
            color={folder === 'papelera' ? 'primary' : 'error'}
            variant="contained"
            disabled={deleting}
          >
            {deleting 
              ? (folder === 'papelera' ? 'Restaurando...' : 'Eliminando...') 
              : (folder === 'papelera' ? 'Restaurar' : 'Eliminar')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailList; 